export type InstallmentLike = {
  value?: string;
  dueDate?: string;
  commission?: string;
  hasCommission?: boolean;
  installmentNumber?: string;
};

export type ItemLike = {
  businessUnit?: string;
  project?: string;
  competenceMonth?: string;
  competenceYear?: string;
  attachments?: unknown[];
  pendingFiles?: unknown[];
};

const isBlank = (value?: string | null): boolean => !value?.trim();

const hasCompetence = (item: ItemLike): boolean =>
  Boolean(item.competenceMonth?.trim()) &&
  Boolean(item.competenceYear?.trim()) &&
  item.competenceYear!.trim().length === 4;

export const isInstallmentStarted = (installment: InstallmentLike): boolean => {
  if (installment.value?.trim()) return true;
  if (installment.dueDate?.trim()) return true;
  if (installment.hasCommission && installment.commission?.trim()) return true;
  return false;
};

export const isInstallmentComplete = (installment: InstallmentLike): boolean =>
  Boolean(installment.value?.trim()) && Boolean(installment.dueDate?.trim());

export const isItemFieldStarted = (item: ItemLike): boolean => {
  if (item.businessUnit?.trim()) return true;
  if (item.project?.trim()) return true;
  if (item.competenceMonth?.trim()) return true;
  if (item.competenceYear?.trim()) return true;
  if ((item.attachments ?? []).length > 0) return true;
  if ((item.pendingFiles ?? []).length > 0) return true;
  return false;
};

export const isItemFieldComplete = (item: ItemLike): boolean =>
  !isBlank(item.businessUnit) &&
  !isBlank(item.project) &&
  hasCompetence(item);

export const ITEM_INCOMPLETE_MESSAGE =
  'Preencha todos os campos do item (BU, Projeto e Competência).';

export const INSTALLMENT_INCOMPLETE_MESSAGE =
  'Informe ao menos uma parcela com valor e vencimento.';

const FIELD_ERROR_PLACEHOLDER = ' ';

export const getInstallmentValidationMessages = (
  installments: InstallmentLike[] | undefined,
  installmentsErrors: unknown,
): string[] => {
  const messages: string[] = [];
  const errors = installmentsErrors as Record<string, unknown> | undefined;
  if (!errors) return messages;

  const list = installments ?? [];
  const incompleteStarted = list
    .map((installment, index) => ({ installment, index }))
    .filter(
      ({ installment }) =>
        isInstallmentStarted(installment) && !isInstallmentComplete(installment),
    );

  incompleteStarted.forEach(({ installment, index }) => {
    const rowErrors = errors[index] as
      | Record<string, { message?: string }>
      | undefined;
    if (!rowErrors) return;

    const label = installment.installmentNumber?.trim() || `Parcela ${index + 1}`;
    const missing: string[] = [];
    if (rowErrors.value) missing.push('valor');
    if (rowErrors.dueDate) missing.push('vencimento');

    if (missing.length > 0) {
      messages.push(`${label}: informe ${missing.join(' e ')}.`);
    }
  });

  if (messages.length === 0 && typeof errors.message === 'string') {
    messages.push(errors.message);
  }

  return messages;
};

export const getItemValidationMessages = (
  items: ItemLike[] | undefined,
  itemsErrors: unknown,
): string[] => {
  const messages: string[] = [];
  const errors = itemsErrors as Record<string, unknown> | undefined;
  if (!errors) return messages;

  const list = items ?? [];
  list.forEach((item, index) => {
    if (!isItemFieldStarted(item) || isItemFieldComplete(item)) return;

    const rowErrors = errors[index] as Record<string, unknown> | undefined;
    if (!rowErrors) return;

    messages.push(`Item ${index + 1}: ${ITEM_INCOMPLETE_MESSAGE}`);
  });

  return messages;
};

export { FIELD_ERROR_PLACEHOLDER };
